import clsx from "clsx";
import type { PuzzleInstance } from "../../../puzzles/core/framework";
import type { SpecPuzzleTemplateId } from "../../../puzzles/core/specTemplates";
import type { ValidationResult } from "../../../puzzles/core/types";
import {
  AngleArc,
  BooleanChoice,
  FormulaBuilder,
  GridCanvas,
  MatrixEditor,
  NumberInput,
  PointMarker,
  PuzzleFeedbackLayer,
  PuzzleHintPanel,
  PuzzleStat,
  ShapeOverlay,
  VectorArrow,
  boundsFromVisual,
  getVisualFlags,
  type GridBounds,
  type Point,
} from "./shared";

export interface SpecPuzzleRendererProps {
  templateId: SpecPuzzleTemplateId;
  instance: PuzzleInstance;
  answer: Record<string, unknown>;
  feedback?: ValidationResult | null;
  visualConfig?: Record<string, unknown>;
  disabled?: boolean;
  running?: boolean;
  onAnswerChange(answer: Record<string, unknown>): void;
}

export function SpecPuzzleRenderer(props: SpecPuzzleRendererProps) {
  switch (props.templateId) {
    case "coordinate-maze":
      return <CoordinateMazeRenderer {...props} />;
    case "binary-beacon":
      return <BinaryBeaconRenderer {...props} />;
    case "vector-cannon":
      return <VectorCannonRenderer {...props} />;
    case "spawn-point-builder":
      return <SpawnPointBuilderRenderer {...props} />;
    case "pathfinding-trail":
      return <PathfindingTrailRenderer {...props} />;
    case "quest-equation":
      return <QuestEquationRenderer {...props} />;
    case "turret-aim":
      return <TurretAimRenderer {...props} />;
    case "transformation-machine":
      return <TransformationMachineRenderer {...props} />;
    case "force-arena":
      return <ForceArenaRenderer {...props} />;
    case "collision-zone":
      return <CollisionZoneRenderer {...props} />;
    case "xp-counter-overflow":
      return <XpCounterOverflowRenderer {...props} />;
    case "sat-inspector":
      return <SatInspectorRenderer {...props} />;
    default:
      return <FallbackRenderer {...props} />;
  }
}

function CoordinateMazeRenderer({
  instance,
  answer,
  disabled,
  visualConfig,
  running,
  feedback,
  onAnswerChange,
}: SpecPuzzleRendererProps) {
  const start = pointState(instance.state.start);
  const target = pointState(instance.state.target);
  const bounds = boundsFromVisual(instance.visual);
  const moves = stringValue(answer.moves);
  const current = parseMoves(start, moves, bounds);
  const setMoves = (nextMoves: string) => onAnswerChange({ ...answer, moves: nextMoves });
  const setDestination = (point: Point) => setMoves(movesFromStart(start, point));
  const appendMove = (direction: string) => {
    const prefix = moves.trim();
    setMoves(prefix ? `${prefix},${direction}1` : `${direction}1`);
  };

  return (
    <RendererShell feedback={feedback} running={running} hints={instance.hints} visualConfig={visualConfig}>
      <GridCanvas
        bounds={bounds}
        selectedPoint={current}
        disabled={disabled}
        visualConfig={visualConfig}
        ariaLabel="Coordinate maze grid. Use arrow keys or click to choose a route destination."
        onPointSelect={setDestination}
      >
        {(projection) => (
          <>
            <PointMarker point={start} projection={projection} label="Start" tone="emerald" />
            <PointMarker point={target} projection={projection} label="Target" tone="amber" />
            <VectorArrow from={start} to={current} projection={projection} color="text-cyan-300" label="route" />
            <PointMarker point={current} projection={projection} label="You" tone="cyan" />
          </>
        )}
      </GridCanvas>
      <div className="grid gap-3">
        <FormulaBuilder label="Moves" value={moves} disabled={disabled} placeholder="E3,N2" onChange={setMoves} />
        <div className="grid grid-cols-4 gap-2" aria-label="Quick move controls">
          {["N", "S", "E", "W"].map((direction) => (
            <button key={direction} type="button" disabled={disabled} onClick={() => appendMove(direction)} className="min-h-10 rounded-lg border border-slate-700 bg-slate-950 text-sm font-bold text-slate-200 hover:border-cyan-300">
              {direction}
            </button>
          ))}
        </div>
      </div>
    </RendererShell>
  );
}

function BinaryBeaconRenderer({ instance, answer, disabled, visualConfig, running, feedback, onAnswerChange }: SpecPuzzleRendererProps) {
  const value = numberState(instance.state.value);
  const binary = stringValue(answer.binary);
  const hex = stringValue(answer.hex);
  return (
    <RendererShell feedback={feedback} running={running} hints={instance.hints} visualConfig={visualConfig}>
      <div className="rounded-xl border border-slate-800 bg-slate-950 p-5">
        <p className="font-retro text-[9px] uppercase tracking-widest text-cyan-300">Beacon payload</p>
        <p className="mt-3 font-mono text-5xl font-black text-white">{value}</p>
        <div className="mt-5 grid grid-cols-4 gap-2" aria-label="Decimal byte preview">
          {byteString(value).map((bit, index) => (
            <span key={index} className="grid min-h-10 place-items-center rounded-lg border border-slate-800 bg-slate-900 font-mono text-sm font-bold text-slate-200">
              {bit}
            </span>
          ))}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormulaBuilder label="Binary" value={binary} disabled={disabled} placeholder="101010" onChange={(next) => onAnswerChange({ ...answer, binary: next })} />
        <FormulaBuilder label="Hex" value={hex} disabled={disabled} placeholder="2A" onChange={(next) => onAnswerChange({ ...answer, hex: next.toUpperCase() })} />
      </div>
    </RendererShell>
  );
}

function VectorCannonRenderer({ instance, answer, disabled, visualConfig, running, feedback, onAnswerChange }: SpecPuzzleRendererProps) {
  const target = pointState(instance.state.target);
  const wind = pointState(instance.state.wind);
  const launch = { x: numberOr(answer.x, 0), y: numberOr(answer.y, 0) };
  const landing = { x: launch.x + wind.x, y: launch.y + wind.y };
  const bounds = paddedBounds([{ x: 0, y: 0 }, target, wind, launch, landing], 10);
  const flags = getVisualFlags(visualConfig);
  const update = (key: string, value: number | "") => onAnswerChange({ ...answer, [key]: value });
  return (
    <RendererShell feedback={feedback} running={running} hints={instance.hints} visualConfig={visualConfig}>
      <GridCanvas bounds={bounds} selectedPoint={launch} disabled={disabled} visualConfig={visualConfig} ariaLabel="Vector cannon aiming plot. Click or use arrow keys to set launch vector." onPointSelect={(point) => onAnswerChange({ ...answer, x: point.x, y: point.y })}>
        {(projection) => (
          <>
            <PointMarker point={{ x: 0, y: 0 }} projection={projection} label="Cannon" tone="slate" />
            <PointMarker point={target} projection={projection} label="Target" tone="amber" />
            {flags.showVectors && <VectorArrow from={{ x: 0, y: 0 }} to={launch} projection={projection} color="text-cyan-300" label="launch" />}
            {flags.showVectors && <VectorArrow from={launch} to={landing} projection={projection} color="text-violet-300" label="wind" dashed />}
            <PointMarker point={landing} projection={projection} label="Landing" tone="cyan" />
          </>
        )}
      </GridCanvas>
      <div className="grid gap-3 sm:grid-cols-2">
        <NumberInput label="Launch X" value={answer.x} disabled={disabled} onChange={(value) => update("x", value)} />
        <NumberInput label="Launch Y" value={answer.y} disabled={disabled} onChange={(value) => update("y", value)} />
      </div>
    </RendererShell>
  );
}

function SpawnPointBuilderRenderer({ instance, answer, disabled, visualConfig, running, feedback, onAnswerChange }: SpecPuzzleRendererProps) {
  const placed = { x: numberOr(answer.x, 0), y: numberOr(answer.y, 0) };
  const bounds = boundsFromVisual(instance.visual);
  const updatePoint = (point: Point) => onAnswerChange({ ...answer, x: point.x, y: point.y });
  return (
    <RendererShell feedback={feedback} running={running} hints={instance.hints} visualConfig={visualConfig}>
      <GridCanvas bounds={bounds} selectedPoint={placed} disabled={disabled} visualConfig={visualConfig} ariaLabel="Spawn point grid. Click, tap, or use arrow keys to move the spawn point." onPointSelect={updatePoint}>
        {(projection) => <PointMarker point={placed} projection={projection} label="Spawn" tone="emerald" radius={2.2} />}
      </GridCanvas>
      <div className="grid gap-3 sm:grid-cols-2">
        <NumberInput label="Spawn X" value={answer.x} disabled={disabled} onChange={(value) => onAnswerChange({ ...answer, x: value })} />
        <NumberInput label="Spawn Y" value={answer.y} disabled={disabled} onChange={(value) => onAnswerChange({ ...answer, y: value })} />
      </div>
    </RendererShell>
  );
}

function PathfindingTrailRenderer({ instance, answer, disabled, visualConfig, running, feedback, onAnswerChange }: SpecPuzzleRendererProps) {
  const points = pointArrayState(instance.state.points);
  const bounds = paddedBounds(points, 2);
  const update = (key: string, value: number | "") => onAnswerChange({ ...answer, [key]: value });
  return (
    <RendererShell feedback={feedback} running={running} hints={instance.hints} visualConfig={visualConfig}>
      <GridCanvas bounds={bounds} visualConfig={visualConfig} ariaLabel="Pathfinding trail plot">
        {(projection) => (
          <>
            <ShapeOverlay shapes={[{ type: "polyline", points, tone: "cyan", label: "trail" }]} projection={projection} />
            {points.map((point, index) => <PointMarker key={index} point={point} projection={projection} label={index === 0 ? "Start" : String(index)} tone={index === points.length - 1 ? "amber" : "emerald"} />)}
            <VectorArrow from={points[0]} to={points[points.length - 1]} projection={projection} color="text-violet-300" label="displacement" dashed />
          </>
        )}
      </GridCanvas>
      <div className="grid gap-3 sm:grid-cols-3">
        <NumberInput label="Total distance" value={answer.distance} disabled={disabled} step={0.1} onChange={(value) => update("distance", value)} />
        <NumberInput label="Displacement X" value={answer.dx} disabled={disabled} onChange={(value) => update("dx", value)} />
        <NumberInput label="Displacement Y" value={answer.dy} disabled={disabled} onChange={(value) => update("dy", value)} />
      </div>
    </RendererShell>
  );
}

function QuestEquationRenderer({ instance, answer, disabled, visualConfig, running, feedback, onAnswerChange }: SpecPuzzleRendererProps) {
  const a = numberState(instance.state.a);
  const b = numberState(instance.state.b);
  const c = numberState(instance.state.c);
  return (
    <RendererShell feedback={feedback} running={running} hints={instance.hints} visualConfig={visualConfig}>
      <div className="rounded-xl border border-slate-800 bg-slate-950 p-5 text-center">
        <p className="font-retro text-[9px] uppercase tracking-widest text-cyan-300">Quest equation</p>
        <p className="mt-3 font-mono text-3xl font-black text-white">
          {a}x {b >= 0 ? "+" : "-"} {Math.abs(b)} = {c}
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_.55fr]">
        <FormulaBuilder label="Scratch formula" value={stringValue(answer.formula)} disabled={disabled} placeholder="(c - b) / a" onChange={(value) => onAnswerChange({ ...answer, formula: value })} />
        <NumberInput label="x" value={answer.x} disabled={disabled} step={0.1} onChange={(value) => onAnswerChange({ ...answer, x: value })} />
      </div>
    </RendererShell>
  );
}

function TurretAimRenderer({ instance, answer, disabled, visualConfig, running, feedback, onAnswerChange }: SpecPuzzleRendererProps) {
  const dx = numberState(instance.state.dx);
  const dy = numberState(instance.state.dy);
  const target = { x: dx, y: dy };
  const angle = numberOr(answer.angle, 0);
  const aim = { x: Math.cos((angle * Math.PI) / 180) * Math.hypot(dx, dy), y: Math.sin((angle * Math.PI) / 180) * Math.hypot(dx, dy) };
  const bounds = paddedBounds([{ x: 0, y: 0 }, target, aim], 5);
  const setAngleFromPoint = (point: Point) => onAnswerChange({ ...answer, angle: Math.round((Math.atan2(point.y, point.x) * 180) / Math.PI) });
  return (
    <RendererShell feedback={feedback} running={running} hints={instance.hints} visualConfig={visualConfig}>
      <GridCanvas bounds={bounds} selectedPoint={aim} disabled={disabled} visualConfig={visualConfig} ariaLabel="Turret aim plot. Click or use arrow keys to adjust angle." onPointSelect={setAngleFromPoint}>
        {(projection) => (
          <>
            <PointMarker point={{ x: 0, y: 0 }} projection={projection} label="Turret" tone="slate" />
            <PointMarker point={target} projection={projection} label="Target" tone="amber" />
            <VectorArrow from={{ x: 0, y: 0 }} to={aim} projection={projection} color="text-cyan-300" label="aim" />
            <AngleArc origin={{ x: 0, y: 0 }} radius={Math.min(dx, dy, 12)} startDegrees={0} endDegrees={angle} projection={projection} label={`${angle} deg`} />
          </>
        )}
      </GridCanvas>
      <NumberInput label="Angle" value={answer.angle} disabled={disabled} unit="degrees" step={0.1} onChange={(value) => onAnswerChange({ ...answer, angle: value })} />
    </RendererShell>
  );
}

function TransformationMachineRenderer({ instance, answer, disabled, visualConfig, running, feedback, onAnswerChange }: SpecPuzzleRendererProps) {
  const point = pointState(instance.state.point);
  const scale = numberState(instance.state.scale);
  const translate = pointState(instance.state.translate);
  const output = { x: numberOr(answer.x, point.x), y: numberOr(answer.y, point.y) };
  const bounds = paddedBounds([point, translate, output, { x: 0, y: 0 }], 4);
  return (
    <RendererShell feedback={feedback} running={running} hints={instance.hints} visualConfig={visualConfig}>
      <GridCanvas bounds={bounds} selectedPoint={output} disabled={disabled} visualConfig={visualConfig} ariaLabel="Transformation output plot. Click or use arrow keys to set final point." onPointSelect={(next) => onAnswerChange({ ...answer, x: next.x, y: next.y })}>
        {(projection) => (
          <>
            <PointMarker point={point} projection={projection} label="Input" tone="emerald" />
            <VectorArrow from={point} to={output} projection={projection} color="text-cyan-300" label="output" />
            <PointMarker point={output} projection={projection} label="Final" tone="cyan" />
          </>
        )}
      </GridCanvas>
      <div className="grid gap-3 sm:grid-cols-[.75fr_1fr]">
        <MatrixEditor label="Transform" rows={2} columns={2} values={[scale, 0, translate.x, translate.y]} disabled onChange={() => undefined} />
        <div className="grid gap-3 sm:grid-cols-2">
          <NumberInput label="Final X" value={answer.x} disabled={disabled} step={0.1} onChange={(value) => onAnswerChange({ ...answer, x: value })} />
          <NumberInput label="Final Y" value={answer.y} disabled={disabled} step={0.1} onChange={(value) => onAnswerChange({ ...answer, y: value })} />
        </div>
      </div>
    </RendererShell>
  );
}

function ForceArenaRenderer({ instance, answer, disabled, visualConfig, running, feedback, onAnswerChange }: SpecPuzzleRendererProps) {
  const mass = numberState(instance.state.mass);
  const acceleration = numberState(instance.state.acceleration);
  const opposingForce = numberState(instance.state.opposingForce);
  const force = numberOr(answer.force, 0);
  const bounds: GridBounds = { xMin: -Math.max(opposingForce, 10), xMax: Math.max(force, opposingForce, 10), yMin: -4, yMax: 4 };
  return (
    <RendererShell feedback={feedback} running={running} hints={instance.hints} visualConfig={visualConfig}>
      <div className="grid gap-3 sm:grid-cols-3">
        <PuzzleStat label="Mass" value={`${mass} kg`} tone="cyan" />
        <PuzzleStat label="Acceleration" value={`${acceleration} m/s^2`} tone="emerald" />
        <PuzzleStat label="Opposing" value={`${opposingForce} N`} tone="amber" />
      </div>
      <GridCanvas bounds={bounds} visualConfig={visualConfig} ariaLabel="Force arena vector diagram">
        {(projection) => (
          <>
            <ShapeOverlay shapes={[{ type: "box", minX: -2, maxX: 2, minY: -1, maxY: 1, tone: "slate", label: "body" }]} projection={projection} />
            <VectorArrow from={{ x: 0, y: 0 }} to={{ x: force, y: 0 }} projection={projection} color="text-cyan-300" label="applied" />
            <VectorArrow from={{ x: 0, y: 0 }} to={{ x: -opposingForce, y: 0 }} projection={projection} color="text-amber-300" label="opposes" />
          </>
        )}
      </GridCanvas>
      <NumberInput label="Applied force" value={answer.force} disabled={disabled} unit="N" step={0.1} onChange={(value) => onAnswerChange({ ...answer, force: value })} />
    </RendererShell>
  );
}

function CollisionZoneRenderer({ instance, answer, disabled, visualConfig, running, feedback, onAnswerChange }: SpecPuzzleRendererProps) {
  const circle = circleState(instance.state.circle);
  const velocity = pointState(instance.state.velocity);
  const box = boxState(instance.state.box);
  const time = numberOr(answer.time, 0);
  const moved = { x: circle.x + velocity.x * time, y: circle.y + velocity.y * time };
  const bounds = paddedBounds([{ x: circle.x, y: circle.y }, moved, { x: box.minX, y: box.minY }, { x: box.maxX, y: box.maxY }], 2);
  return (
    <RendererShell feedback={feedback} running={running} hints={instance.hints} visualConfig={visualConfig}>
      <GridCanvas bounds={bounds} visualConfig={visualConfig} ariaLabel="Collision zone diagram">
        {(projection) => (
          <>
            <ShapeOverlay shapes={[{ type: "box", ...box, tone: "amber", label: "box" }, { type: "circle", center: { x: circle.x, y: circle.y }, radius: circle.radius, tone: "cyan", label: "start" }, { type: "circle", center: moved, radius: circle.radius, tone: "violet", label: "at time" }]} projection={projection} />
            <VectorArrow from={{ x: circle.x, y: circle.y }} to={{ x: circle.x + velocity.x, y: circle.y + velocity.y }} projection={projection} color="text-emerald-300" label="v" />
          </>
        )}
      </GridCanvas>
      <div className="grid gap-3 sm:grid-cols-2">
        <BooleanChoice label="Will collide within 3 seconds?" value={answer.willCollide} disabled={disabled} onChange={(value) => onAnswerChange({ ...answer, willCollide: value })} />
        <NumberInput label="Contact time" value={answer.time} disabled={disabled} unit="s" step={0.1} onChange={(value) => onAnswerChange({ ...answer, time: value })} />
      </div>
    </RendererShell>
  );
}

function XpCounterOverflowRenderer({ instance, answer, disabled, visualConfig, running, feedback, onAnswerChange }: SpecPuzzleRendererProps) {
  const bits = numberState(instance.state.bits);
  const maxValue = numberState(instance.state.maxValue);
  const current = numberState(instance.state.current);
  const gain = numberState(instance.state.gain);
  const predicted = numberOr(answer.finalValue, current);
  const fill = Math.max(0, Math.min(100, (predicted / Math.max(1, maxValue)) * 100));
  return (
    <RendererShell feedback={feedback} running={running} hints={instance.hints} visualConfig={visualConfig}>
      <div className="grid gap-3 sm:grid-cols-3">
        <PuzzleStat label="Bits" value={bits} tone="cyan" />
        <PuzzleStat label="Current" value={current} tone="emerald" />
        <PuzzleStat label="Gain" value={`+${gain}`} tone="amber" />
      </div>
      <div className="rounded-xl border border-slate-800 bg-slate-950 p-5">
        <div className="mb-2 flex justify-between text-xs font-bold text-slate-400">
          <span>0</span>
          <span>Max {maxValue}</span>
        </div>
        <div className="h-7 overflow-hidden rounded-lg border border-slate-800 bg-slate-900" role="meter" aria-valuemin={0} aria-valuemax={maxValue} aria-valuenow={predicted}>
          <div className={clsx("h-full bg-cyan-300 transition-[width]", getVisualFlags(visualConfig).animate && "duration-500")} style={{ width: `${fill}%` }} />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <BooleanChoice label="Overflows?" value={answer.wraps} disabled={disabled} onChange={(value) => onAnswerChange({ ...answer, wraps: value })} />
        <NumberInput label="Final stored value" value={answer.finalValue} disabled={disabled} onChange={(value) => onAnswerChange({ ...answer, finalValue: value })} />
      </div>
    </RendererShell>
  );
}

function SatInspectorRenderer({ instance, answer, disabled, visualConfig, running, feedback, onAnswerChange }: SpecPuzzleRendererProps) {
  const a = boxState(instance.state.a);
  const b = boxState(instance.state.b);
  const bounds = paddedBounds([{ x: a.minX, y: a.minY }, { x: a.maxX, y: a.maxY }, { x: b.minX, y: b.minY }, { x: b.maxX, y: b.maxY }], 2);
  return (
    <RendererShell feedback={feedback} running={running} hints={instance.hints} visualConfig={visualConfig}>
      <GridCanvas bounds={bounds} visualConfig={visualConfig} ariaLabel="SAT inspector box projection diagram">
        {(projection) => <ShapeOverlay shapes={[{ type: "box", ...a, tone: "cyan", label: "A" }, { type: "box", ...b, tone: "amber", label: "B" }]} projection={projection} />}
      </GridCanvas>
      <div className="grid gap-3 sm:grid-cols-2">
        <BooleanChoice label="Separated?" value={answer.separated} disabled={disabled} onChange={(value) => onAnswerChange({ ...answer, separated: value })} />
        <label className="block rounded-xl border border-slate-800 bg-slate-950/55 p-3 text-xs font-bold text-slate-300">
          Separating axis
          <select value={stringValue(answer.axis)} disabled={disabled} onChange={(event) => onAnswerChange({ ...answer, axis: event.target.value })} className="mt-2 min-h-11 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 text-sm text-white">
            <option value="">Choose...</option>
            <option value="x">x</option>
            <option value="y">y</option>
            <option value="none">none</option>
          </select>
        </label>
      </div>
    </RendererShell>
  );
}

function FallbackRenderer({ instance, answer, disabled, visualConfig, running, feedback, onAnswerChange }: SpecPuzzleRendererProps) {
  return (
    <RendererShell feedback={feedback} running={running} hints={instance.hints} visualConfig={visualConfig}>
      <div className="grid gap-3">
        {instance.expectedAnswer.fields.map((field) =>
          field.type === "boolean" ? (
            <BooleanChoice key={field.key} label={field.label} value={answer[field.key]} disabled={disabled} onChange={(value) => onAnswerChange({ ...answer, [field.key]: value })} />
          ) : field.type === "choice" ? (
            <label key={field.key} className="block rounded-xl border border-slate-800 bg-slate-950/55 p-3 text-xs font-bold text-slate-300">
              {field.label}
              <select value={stringValue(answer[field.key])} disabled={disabled} onChange={(event) => onAnswerChange({ ...answer, [field.key]: event.target.value })} className="mt-2 min-h-11 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 text-sm text-white">
                <option value="">Choose...</option>
                {field.choices.map((choice) => <option key={choice.id} value={choice.id}>{choice.label}</option>)}
              </select>
            </label>
          ) : field.type === "number" ? (
            <NumberInput key={field.key} label={field.label} value={answer[field.key]} disabled={disabled} unit={field.unit} step={field.step} onChange={(value) => onAnswerChange({ ...answer, [field.key]: value })} />
          ) : (
            <FormulaBuilder key={field.key} label={field.label} value={stringValue(answer[field.key])} disabled={disabled} placeholder={field.placeholder} onChange={(value) => onAnswerChange({ ...answer, [field.key]: value })} />
          ),
        )}
      </div>
    </RendererShell>
  );
}

function RendererShell({
  children,
  feedback,
  running,
  hints,
  visualConfig,
}: {
  children: React.ReactNode;
  feedback?: ValidationResult | null;
  running?: boolean;
  hints: string[];
  visualConfig?: Record<string, unknown>;
}) {
  return (
    <div className="grid gap-4">
      {children}
      <PuzzleFeedbackLayer feedback={feedback} running={running} />
      <PuzzleHintPanel hints={hints} visualConfig={visualConfig} />
    </div>
  );
}

function numberState(value: unknown, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function numberOr(value: unknown, fallback: number) {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function stringValue(value: unknown) {
  return typeof value === "string" ? value : value == null ? "" : String(value);
}

function pointState(value: unknown): Point {
  if (value && typeof value === "object" && "x" in value && "y" in value) {
    return { x: numberState((value as Point).x), y: numberState((value as Point).y) };
  }
  return { x: 0, y: 0 };
}

function pointArrayState(value: unknown): Point[] {
  return Array.isArray(value) ? value.map(pointState) : [{ x: 0, y: 0 }];
}

function circleState(value: unknown) {
  if (value && typeof value === "object") {
    const source = value as { x?: unknown; y?: unknown; radius?: unknown };
    return { x: numberState(source.x), y: numberState(source.y), radius: numberState(source.radius, 1) };
  }
  return { x: 0, y: 0, radius: 1 };
}

function boxState(value: unknown) {
  if (value && typeof value === "object") {
    const source = value as { minX?: unknown; maxX?: unknown; minY?: unknown; maxY?: unknown };
    return {
      minX: numberState(source.minX),
      maxX: numberState(source.maxX),
      minY: numberState(source.minY),
      maxY: numberState(source.maxY),
    };
  }
  return { minX: 0, maxX: 1, minY: 0, maxY: 1 };
}

function paddedBounds(points: Point[], padding: number): GridBounds {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  return {
    xMin: Math.floor(Math.min(...xs) - padding),
    xMax: Math.ceil(Math.max(...xs) + padding),
    yMin: Math.floor(Math.min(...ys) - padding),
    yMax: Math.ceil(Math.max(...ys) + padding),
  };
}

function parseMoves(start: Point, moves: string, bounds: GridBounds) {
  return moves
    .toUpperCase()
    .split(",")
    .reduce((position, move) => {
      const match = /^([NSEW])(\d+)$/.exec(move.trim());
      if (!match) return position;
      const amount = Number(match[2]);
      const next =
        match[1] === "N"
          ? { ...position, y: position.y + amount }
          : match[1] === "S"
            ? { ...position, y: position.y - amount }
            : match[1] === "E"
              ? { ...position, x: position.x + amount }
              : { ...position, x: position.x - amount };
      return {
        x: Math.max(bounds.xMin, Math.min(bounds.xMax, next.x)),
        y: Math.max(bounds.yMin, Math.min(bounds.yMax, next.y)),
      };
    }, start);
}

function movesFromStart(start: Point, point: Point) {
  const dx = point.x - start.x;
  const dy = point.y - start.y;
  const parts = [];
  if (dx !== 0) parts.push(`${dx > 0 ? "E" : "W"}${Math.abs(dx)}`);
  if (dy !== 0) parts.push(`${dy > 0 ? "N" : "S"}${Math.abs(dy)}`);
  return parts.join(",");
}

function byteString(value: number) {
  return value.toString(2).padStart(8, "0").split("");
}
